import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Select, Switch, Drawer, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { AxiosError } from "axios";
import {
  creditorPost,
  getAllCreditors,
  supplierPost,
  getAllSuppliers,
} from "@apis";
import { SupplierI, CreditorI } from "@interfaces";
import "../../css/creditor-supplier-selector.css";

const { Option } = Select;

interface PropsI {
  supplierId: string | null;
  creditorId: string | null;
  isCreditor: boolean;
  onSupplierChange: (id: string | null) => void;
  onCreditorChange: (id: string | null) => void;
  onTypeChange: (isCreditor: boolean) => void;
}

export const CreditorSupplierSelector = ({
  supplierId,
  creditorId,
  isCreditor,
  onSupplierChange,
  onCreditorChange,
  onTypeChange,
}: PropsI) => {
  const [suppliers, setSuppliers] = React.useState<SupplierI[]>([]);
  const [creditors, setCreditors] = React.useState<CreditorI[]>([]);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [isProcessing, setProcessing] = React.useState(false);

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [suppRes, credRes] = await Promise.all([
        getAllSuppliers(),
        getAllCreditors(),
      ]);
      setSuppliers(suppRes.data ?? []);
      setCreditors(credRes.data ?? []);
    } catch (err: any) {
      const axiosError = err as AxiosError;
      message.error(axiosError.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      docNumber: "",
      notes: "",
      statusSupplierBoolean: true,
      statusCreditorBoolean: true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("El nombre es requerido"),
      phone: Yup.string()
        .nullable()
        .optional()
        .test(
          "phone-length",
          "El teléfono debe tener exactamente 9 caracteres, quite el 0 al principio",
          (value) => !value || value.length === 9,
        ),
      email: Yup.string().email("Correo inválido").nullable().optional(),
      docNumber: Yup.string().nullable().optional(),
      notes: Yup.string().nullable().optional(),
    }),
    onSubmit: async (values) => {
      try {
        setProcessing(true);
        if (isCreditor) {
          const { data } = await creditorPost({
            name: values.name,
            phone: values.phone,
            email: values.email,
            docNumber: values.docNumber,
            notes: values.notes,
            statusCreditorBoolean: values.statusCreditorBoolean,
          });
          message.success(data);
        } else {
          const { data } = await supplierPost({
            name: values.name,
            phone: values.phone,
            email: values.email,
            docNumber: values.docNumber,
            notes: values.notes,
            contact: "",
            address: "",
            statusSupplierBoolean: values.statusSupplierBoolean,
          });
          message.success(data);
        }
        await loadData();
        setDrawerOpen(false);
        formik.resetForm();
      } catch (err: any) {
        const axiosError = err as AxiosError;
        message.error(axiosError.message);
      } finally {
        setProcessing(false);
      }
    },
  });

  const handleTypeChange = (checked: boolean) => {
    onTypeChange(checked);
    onSupplierChange(null);
    onCreditorChange(null);
  };

  const handleOpenDrawer = () => {
    formik.resetForm();
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    formik.resetForm();
  };

  return (
    <div className="css-selector-wrapper">
      {/* switch */}
      <div
        className={`css-switch-row ${isCreditor ? "css-switch-row-creditor" : "css-switch-row-supplier"}`}
      >
        <div className="css-switch-info">
          <span className="css-switch-title">Tipo de entidad</span>
          <span className="css-switch-sub">
            ¿A quién le corresponde este gasto?
          </span>
        </div>
        <div className="css-switch-controls">
          <span
            className={`css-switch-lbl ${!isCreditor ? "css-switch-lbl-active-supplier" : "css-switch-lbl-inactive"}`}
          >
            Proveedor
          </span>
          <Switch
            checked={isCreditor}
            onChange={handleTypeChange}
            className={
              isCreditor ? "css-switch-creditor" : "css-switch-supplier"
            }
          />
          <span
            className={`css-switch-lbl ${isCreditor ? "css-switch-lbl-active-creditor" : "css-switch-lbl-inactive"}`}
          >
            Acreedor
          </span>
        </div>
      </div>

      {/* select + botón agregar */}
      <div className="css-select-row">
        <Select
          className="css-select"
          placeholder={
            isCreditor
              ? "— Selecciona un acreedor —"
              : "— Selecciona un proveedor —"
          }
          value={isCreditor ? creditorId : supplierId}
          onChange={(v) =>
            isCreditor ? onCreditorChange(v) : onSupplierChange(v)
          }
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {isCreditor
            ? creditors.map((c) => (
                <Option key={c.id} value={String(c.id)}>
                  {c.name}
                </Option>
              ))
            : suppliers.map((s) => (
                <Option key={s.id} value={String(s.id)}>
                  {s.name}
                </Option>
              ))}
        </Select>
        <button
          type="button"
          className={`css-btn-add ${isCreditor ? "css-btn-add-creditor" : "css-btn-add-supplier"}`}
          onClick={handleOpenDrawer}
        >
          <PlusOutlined style={{ fontSize: 11 }} />
          {isCreditor ? "Nuevo Acreedor" : "Nuevo Proveedor"}
        </button>
      </div>
      <span className="css-hint">
        Deja vacío si el gasto no tiene entidad asociada
      </span>

      {/* drawer */}
      <Drawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        width={340}
        title={null}
        closable={false}
        className="css-drawer"
        zIndex={1002}
      >
        <div className="css-drawer-body">
          {/* header drawer */}
          <div className="css-drawer-header">
            <div className="css-drawer-header-left">
              <span className="css-drawer-title">
                {isCreditor ? "Nuevo acreedor" : "Nuevo proveedor"}
              </span>
              <span
                className={`css-drawer-badge ${isCreditor ? "css-drawer-badge-creditor" : "css-drawer-badge-supplier"}`}
              >
                {isCreditor ? "Acreedor" : "Proveedor"}
              </span>
            </div>
            <button className="css-drawer-close" onClick={handleCloseDrawer}>
              ✕
            </button>
          </div>

          {/* form */}
          <form onSubmit={formik.handleSubmit} className="css-drawer-form">
            {/* nombre */}
            <div className="css-drawer-field">
              <label className="css-drawer-label">Nombre</label>
              <input
                className={`css-drawer-input ${formik.touched.name && formik.errors.name ? "css-drawer-input-error" : ""}`}
                placeholder={isCreditor ? "Empresa Eléctrica" : "FrioMax S.A."}
                value={formik.values.name}
                onChange={(e) => formik.setFieldValue("name", e.target.value)}
                onBlur={(e) =>
                  formik.setFieldValue("name", e.target.value.trim())
                }
              />
              {formik.touched.name && formik.errors.name && (
                <span className="css-drawer-error">{formik.errors.name}</span>
              )}
            </div>

            {/* RUC */}
            <div className="css-drawer-field">
              <label className="css-drawer-label">RUC / Cédula</label>
              <input
                className="css-drawer-input"
                placeholder="0900000000001"
                value={formik.values.docNumber}
                onChange={(e) =>
                  formik.setFieldValue("docNumber", e.target.value)
                }
                onBlur={(e) =>
                  formik.setFieldValue("docNumber", e.target.value.trim())
                }
              />
            </div>

            {/* teléfono y email */}
            <div className="css-drawer-field-row">
              <div className="css-drawer-field">
                <label className="css-drawer-label">Teléfono</label>
                <div
                  className={`css-prefix-wrap ${formik.touched.phone && formik.errors.phone ? "css-prefix-error" : ""}`}
                >
                  <span className="css-prefix">+593</span>
                  <input
                    className="css-prefix-input"
                    placeholder="991234567"
                    value={formik.values.phone}
                    onChange={(e) =>
                      formik.setFieldValue("phone", e.target.value)
                    }
                    onBlur={(e) =>
                      formik.setFieldValue("phone", e.target.value.trim())
                    }
                  />
                </div>
                {formik.touched.phone && formik.errors.phone && (
                  <span className="css-drawer-error">
                    {formik.errors.phone}
                  </span>
                )}
              </div>
              <div className="css-drawer-field">
                <label className="css-drawer-label">Correo</label>
                <input
                  type="email"
                  className={`css-drawer-input ${formik.touched.email && formik.errors.email ? "css-drawer-input-error" : ""}`}
                  placeholder="contacto@mail.com"
                  value={formik.values.email}
                  onChange={(e) =>
                    formik.setFieldValue("email", e.target.value)
                  }
                  onBlur={(e) =>
                    formik.setFieldValue("email", e.target.value.trim())
                  }
                />
                {formik.touched.email && formik.errors.email && (
                  <span className="css-drawer-error">
                    {formik.errors.email}
                  </span>
                )}
              </div>
            </div>

            {/* notas */}
            <div className="css-drawer-field">
              <label className="css-drawer-label">Notas</label>
              <textarea
                className="css-drawer-input css-drawer-textarea"
                placeholder="Ej: pago mensual servicio eléctrico..."
                rows={2}
                value={formik.values.notes}
                onChange={(e) => formik.setFieldValue("notes", e.target.value)}
                onBlur={(e) =>
                  formik.setFieldValue("notes", e.target.value.trim())
                }
              />
            </div>

            {/* switch activo */}
            <div className="css-drawer-switch-row">
              <div className="css-drawer-switch-info">
                <span className="css-drawer-switch-title">
                  {isCreditor ? "Acreedor activo" : "Proveedor activo"}
                </span>
              </div>
              <Switch
                checked={
                  isCreditor
                    ? formik.values.statusCreditorBoolean
                    : formik.values.statusSupplierBoolean
                }
                onChange={(v) =>
                  formik.setFieldValue(
                    isCreditor
                      ? "statusCreditorBoolean"
                      : "statusSupplierBoolean",
                    v,
                  )
                }
                className={`css-drawer-switch ${isCreditor ? "css-drawer-switch-creditor" : "css-drawer-switch-supplier"}`}
              />
            </div>

            {/* acciones */}
            <div className="css-drawer-actions">
              <button
                type="button"
                className="css-drawer-btn-cancel"
                onClick={handleCloseDrawer}
                disabled={isProcessing}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={`css-drawer-btn-save ${isCreditor ? "css-drawer-btn-save-creditor" : "css-drawer-btn-save-supplier"}`}
                disabled={isProcessing}
              >
                {isProcessing ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </Drawer>
    </div>
  );
};
